const SIGNAL_STALE_MS = 24 * 60 * 60 * 1000;

function hasValue(value) {
    return value !== null && value !== undefined && !(typeof value === "string" && value.trim() === "");
}

function finiteNumber(value) {
    if (!hasValue(value)) return null;
    const num = typeof value === "number" ? value : Number(value);
    return Number.isFinite(num) ? num : null;
}

function textValue(...values) {
    for (const value of values) {
        if (hasValue(value)) return String(value);
    }
    return null;
}

function normalizeSignalState(value) {
    if (!hasValue(value)) return null;
    const normalized = String(value).trim().toUpperCase();
    return normalized || null;
}

function normalizeTimestamp(value, nowMs = Date.now()) {
    if (!hasValue(value)) {
        return { label: "unknown", stale: true, staleReason: "missing" };
    }
    const text = String(value).trim();
    const parsed = Date.parse(text);
    if (!Number.isFinite(parsed)) {
        return { label: text, stale: true, staleReason: "invalid" };
    }
    const label = new Date(parsed).toISOString().replace(".000Z", "Z");
    const stale = nowMs - parsed > SIGNAL_STALE_MS;
    return { label, stale, staleReason: stale ? "old" : null };
}

function normalizeMode(row = {}, signal = {}) {
    return textValue(
        signal.execution_mode,
        signal.mode,
        signal.trading_mode,
        signal.runtime_mode,
        signal.source,
        row.execution_mode,
        row.mode,
        row.trading_mode,
        row.runtime_mode,
        row.source,
    );
}

function normalizeScore(row = {}, signal = {}) {
    const score = finiteNumber(signal.score ?? row.score);
    const confidence = finiteNumber(signal.confidence ?? row.confidence);
    const strength = finiteNumber(signal.strength ?? signal.signal_strength ?? row.strength ?? row.signal_strength);
    return { score, confidence, strength };
}

function unwrapLoggerRow(row) {
    if (!row || typeof row !== "object" || Array.isArray(row)) return null;
    const data = row.data;
    if (!data || typeof data !== "object" || Array.isArray(data)) return row;
    return {
        ...row,
        ...data,
        timestamp: hasValue(data.timestamp) ? data.timestamp : row.timestamp,
        created_at: hasValue(data.created_at) ? data.created_at : row.created_at,
        source: hasValue(data.source) ? data.source : row.source,
        rawWrapper: row,
    };
}

function normalizeEntry(row, nestedKey = null, nestedSignal = null, nowMs = Date.now()) {
    if (!row || typeof row !== "object" || Array.isArray(row)) return null;
    const signal = nestedSignal && typeof nestedSignal === "object" && !Array.isArray(nestedSignal) ? nestedSignal : row;
    const coin = textValue(signal.coin, signal.symbol, nestedKey, row.coin, row.symbol);
    const state = normalizeSignalState(signal.signal ?? signal.action ?? signal.status ?? signal.signal_type ?? row.signal ?? row.action ?? row.status ?? row.signal_type);
    const timestamp = normalizeTimestamp(signal.timestamp ?? signal.created_at ?? row.timestamp ?? row.created_at, nowMs);
    const score = normalizeScore(row, signal);
    const engine = textValue(signal.engine, signal.module, row.engine, row.module, signal.source, row.source);
    const mode = normalizeMode(row, signal);
    const reason = textValue(signal.reason, signal.message, row.reason, row.message);

    return {
        coin,
        state,
        timestamp,
        score,
        engine,
        mode,
        reason,
        raw: row.rawWrapper || row,
    };
}

function payloadEntries(payload) {
    if (Array.isArray(payload)) return payload;
    if (!payload || typeof payload !== "object") return null;
    if (Array.isArray(payload.entries)) return payload.entries;
    if (Array.isArray(payload.data)) return payload.data;
    if (payload.data && typeof payload.data === "object" && Array.isArray(payload.data.entries)) return payload.data.entries;
    return null;
}

export function normalizeSignalLogResponse(apiResult, { nowMs = Date.now() } = {}) {
    if (!apiResult || apiResult.ok === false || apiResult.unauth) {
        return { status: "unavailable", entries: [] };
    }
    const entries = payloadEntries(apiResult.data ?? apiResult);
    if (!entries) return { status: "malformed", entries: [] };

    const normalized = [];
    for (const row of entries) {
        if (!row || typeof row !== "object" || Array.isArray(row)) return { status: "malformed", entries: [] };
        const normalizedRow = unwrapLoggerRow(row);
        if (!normalizedRow) return { status: "malformed", entries: [] };
        if (Object.prototype.hasOwnProperty.call(normalizedRow, "signals")) {
            if (!normalizedRow.signals || typeof normalizedRow.signals !== "object" || Array.isArray(normalizedRow.signals)) {
                return { status: "malformed", entries: [] };
            }
            for (const [key, signal] of Object.entries(normalizedRow.signals)) {
                const entry = normalizeEntry(normalizedRow, key, signal, nowMs);
                if (entry) normalized.push(entry);
            }
            continue;
        }
        const entry = normalizeEntry(normalizedRow, null, null, nowMs);
        if (entry) normalized.push(entry);
    }

    return { status: normalized.length ? "ready" : "empty", entries: normalized };
}

export function signalValueLabel(value) {
    return value === null || value === undefined ? "—" : String(value);
}

export function SIGNAL_STALE_THRESHOLD_MS() {
    return SIGNAL_STALE_MS;
}
